import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  getRiskClassification,
  getRisks,
  getRisksForDisplay,
  colorByRisks,
} from '@util/classificationUtil'
import { hexToRgbA } from '@util/stringUtil'
import BarChart from '@generators/htmlComponents/icons/BarChart'
import colors from '@settings/styleSettings'
import { Box } from './Box'
import Text from './Text'

const HEALTH_MESSAGES = {
  has: 'O controle da hipertensão é essencial para a saúde cardiovascular.',
  dm: 'O monitoramento da glicemia é fundamental para o controle do diabetes.',
  cholesterol: 'Níveis de colesterol controlados reduzem riscos cardíacos.',
  overweight: 'A gestão do peso corporal auxilia na prevenção de diversas patologias.',
}

export const RiskClassification = ({
  has, dm, cholesterol, imc,
}) => {
  const risks = getRisks(has, dm, cholesterol, imc)
  const risksLength = Array.isArray(risks) ? risks.length : risks
  const riskClassification = getRiskClassification(risksLength)
  const riskColor = colorByRisks(risksLength)

  const descriptionText = useMemo(() => {
    if (!Array.isArray(risks) || risks.length === 0) {
      if (risksLength < 0) {
        return (
          <>
            Não foi possível classificar seu risco por falta de dados coletados.
            <br />
            <br />
            Procure realizar a coleta para receber orientações mais precisas.
          </>
        )
      }
      return (
        <>
          Seus índices estão dentro da normalidade.
          <br />
          <br />
          Mantenha hábitos saudáveis como alimentação equilibrada
          e exercícios físicos para continuar assim.
        </>
      )
    }

    const messages = []
    if (has) messages.push(HEALTH_MESSAGES.has)
    if (dm) messages.push(HEALTH_MESSAGES.dm)
    if (cholesterol) messages.push(HEALTH_MESSAGES.cholesterol)
    if (imc > 25) messages.push(HEALTH_MESSAGES.overweight)

    return (
      <>
        Identificamos alterações nos seus índices
        {getRisksForDisplay(risks)}
        <br />
        <br />
        {messages.join(' ')}
        <br />
        <br />
        Esses fatores exigem cuidados para não evoluírem.
        Confira abaixo as orientações preventivas.
      </>
    )
  }, [risks, has, dm, cholesterol, imc])

  return (
    <div style={styles.container}>
      <Box width={188} height={98} padding={12} align="center">
        <div style={styles.riskContainer}>
          <div
            style={{
              ...styles.iconContainer,
              backgroundColor: hexToRgbA(riskColor, '0.16'),
            }}
          >
            <BarChart color={riskColor} />
          </div>
          <div>
            <Text
              style={{
                color: colors.strongGray,
                fontSize: 'inherit',
                margin: '12px 0 0 0',
              }}
            >
              Classificação de risco
            </Text>
            <Text
              style={{
                ...styles.riskText,
                color: riskColor,
                fontFamily: 'Inter-Bold',
                fontWeight: 700,
              }}
            >
              {riskClassification}
            </Text>
          </div>
        </div>
      </Box>
      <Box width={343} padding={12}>
        <Text style={{
          color: colors.strongGray, fontSize: 'inherit', margin: 0, textAlign: 'left',
        }}
        >
          Seu risco atual é
          {' '}
          <b>{riskClassification}</b>
          <br />
          <br />
          {descriptionText}
        </Text>
      </Box>
    </div>
  )
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '24px 0',
    gap: 16,
  },
  riskContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
    justifyContent: 'space-between',
    width: '100%',
    height: '100%',
  },
  iconContainer: {
    alignSelf: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 6,
    borderRadius: '50%',
    width: 40,
    height: 40,
  },
  riskText: {
    fontSize: 'inherit',
    margin: 0,
  },
}

RiskClassification.propTypes = {
  has: PropTypes.bool.isRequired,
  dm: PropTypes.bool.isRequired,
  cholesterol: PropTypes.bool.isRequired,
  imc: PropTypes.number.isRequired,
}
